<template>
  <div class="tab3">
    <b-overlay :show="loading">

      <!-- Section 1: Tổng trạng ban đầu -->
      <b-card class="metrics-card mb-4">
        <div slot="header" class="metrics-title">
          <i class="fas fa-wave-square"></i>
          <span>Tổng trạng ban đầu</span>
        </div>
        <div class="metrics-grid">
          <BasicInput v-model="baselineForm.height" label="Chiều cao (cm)" name="b_height" type="number" @input="calcBaselineBmi" />
          <BasicInput v-model="baselineForm.weight" label="Cân nặng thực tế (kg)" name="b_weight" type="number" @input="calcBaselineBmi" />
          <BasicInput v-model="baselineForm.bmi" label="BMI (Kg/m²)" name="b_bmi" type="number" disabled />
          <BasicInput v-model="baselineForm.waist" label="Vòng eo (cm)" name="b_waist" type="number" />
          <BasicInput v-model="baselineForm.hip" label="Vòng mông (cm)" name="b_hip" type="number" />
          <BasicInput v-model="baselineForm.muscleMass" label="Khối lượng Cơ (kg)" name="b_muscleMass" type="number" />
          <BasicInput v-model="baselineForm.fatMass" label="Khối lượng Mỡ (kg)" name="b_fatMass" type="number" />
          <BasicInput v-model="baselineForm.bodyFatPercentage" label="Tỷ lệ Mỡ (%)" name="b_bodyFatPercentage" type="number" />
          <BasicInput v-model="baselineForm.visceralFatLevel" label="Mỡ nội tạng (Level)" name="b_visceralFatLevel" type="number" />
          <BasicInput v-model="baselineForm.waterMass" label="Khối lượng Nước (kg)" name="b_waterMass" type="number" />
          <BasicInput v-model="baselineForm.systolicBloodPressure" label="Huyết áp tâm thu (mmHg)" name="b_systolicBloodPressure" type="number" />
          <BasicInput v-model="baselineForm.diastolicBloodPressure" label="Huyết áp tâm trương (mmHg)" name="b_diastolicBloodPressure" type="number" />
          <BasicInput v-model="baselineForm.heartRate" label="Mạch / Nhịp tim (lần/phút)" name="b_heartRate" type="number" />
          <BasicInput v-model="baselineForm.fastingBloodSugar" label="Đường huyết đói (mmol/L)" name="b_fbs" type="number" />
          <BasicInput v-model="baselineForm.postprandialBloodSugar" label="Đường huyết sau ăn (mmol/L)" name="b_ppbs" type="number" />
          <BasicInput v-model="baselineForm.hba1c" label="HbA1c (%)" name="b_hba1c" type="number" />
        </div>
        <BasicTextArea v-model="baselineForm.additionalNotes" label="Chỉ số bổ sung & ghi chú" name="b_notes" />
        <div class="text-right mt-3">
          <LoadingButton v-if="isUpdatePermission" class="btn btn-success" :handler="saveBaseline">Lưu tổng trạng ban đầu</LoadingButton>
        </div>
      </b-card>

      <!-- Section 2: Ghi chú mục tiêu -->
      <b-card class="mb-4">
        <h6 slot="header">Mục tiêu sau chương trình</h6>
        <b-row>
          <b-col md="4"><BasicInput v-model="goalForm.weightGoal" label="Cân nặng mục tiêu (kg)" name="g_wg" type="number" @input="calcGoalBmi" /></b-col>
          <b-col md="4"><BasicInput v-model="goalForm.bmiGoal" label="BMI mục tiêu" name="g_bg" type="number" disabled /></b-col>
          <b-col md="4"><BasicInput v-model="goalForm.waistGoal" label="Vòng eo mục tiêu (cm)" name="g_wst" type="number" /></b-col>
          <b-col md="4"><BasicInput v-model="goalForm.hba1cGoal" label="HbA1C mục tiêu (%)" name="g_hba" type="number" /></b-col>
          <b-col md="4"><BasicInput v-model="goalForm.fastingBloodSugarGoal" label="ĐH đói mục tiêu (mmol/L)" name="g_fbs" type="number" /></b-col>
          <b-col md="4"><BasicInput v-model="goalForm.bloodPressureGoal" label="Huyết áp mục tiêu (mmHg)" name="g_bp" placeholder="VD: 120/80" /></b-col>
          <b-col md="12"><BasicTextArea v-model="goalForm.goalNote" label="Ghi chú mục tiêu" name="g_note" /></b-col>
        </b-row>
        <div class="text-right mt-3">
          <LoadingButton v-if="isUpdatePermission" class="btn btn-success" :handler="saveGoalNote">Lưu ghi chú mục tiêu</LoadingButton>
        </div>
      </b-card>

      <!-- Section 3: Kế hoạch hành động -->
      <b-card class="mb-4">
        <div slot="header" class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Kế hoạch hành động thay đổi lối sống</h6>
          <b-button v-if="isInsertPermission" size="sm" variant="outline-success" @click="addActionPlan">
            <i class="fas fa-plus mr-1"></i>Thêm hành động
          </b-button>
        </div>
        <div v-for="(plan, idx) in actionPlans" :key="plan._uid" class="action-plan-card mb-3 p-3 border rounded">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex align-items-center">
              <span class="plan-index bg-success">{{ idx + 1 }}</span>
              <span v-if="plan.dirty" class="badge badge-warning ml-2">Chưa lưu</span>
              <span v-else class="badge badge-success ml-2">Đã lưu</span>
            </div>
            <div>
              <LoadingButton v-if="isUpdatePermission" class="btn btn-sm btn-success mr-1" :handler="() => saveOnePlan(idx)">
                <i class="fas fa-save"></i>
              </LoadingButton>
              <button v-if="isDeletePermission" type="button" class="btn btn-sm btn-outline-danger" @click="removeActionPlan(idx)">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <b-row>
            <b-col md="12"><BasicInput v-model="plan.action" label="Hành động" :name="'ac-'+idx" @input="() => markDirty(idx)" /></b-col>
            <b-col md="4"><BasicInput v-model="plan.frequency" label="Tần suất" :name="'fr-'+idx" type="number" :input-number="true" @input="() => markDirty(idx)" /></b-col>
            <b-col md="4"><BasicInput v-model="plan.unit" label="Đơn vị" :name="'un-'+idx" @input="() => markDirty(idx)" /></b-col>
            <b-col md="4"><BasicInput v-model="plan.note" label="Ghi chú" :name="'nt-'+idx" @input="() => markDirty(idx)" /></b-col>
          </b-row>
        </div>
        <div v-if="!actionPlans.length" class="text-center text-muted py-3">
          Chưa có kế hoạch hành động. Nhấn "Thêm hành động" để tạo mới.
        </div>
      </b-card>

      <!-- Section 4: Thực đơn mẫu -->
      <b-card class="mb-4">
        <div slot="header" class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Thực đơn mẫu</h6>
          <b-button v-if="isUpdatePermission" size="sm" variant="outline-success" :disabled="sampleMenuUploading" @click="$refs.sampleMenuInput.click()">
            <b-spinner v-if="sampleMenuUploading" small class="mr-1" />
            <i v-else class="fas fa-paperclip mr-1"></i>Đính kèm file
          </b-button>
          <input
            ref="sampleMenuInput"
            type="file"
            multiple
            class="d-none"
            :accept="sampleMenuAccept"
            @change="onSampleMenuSelected"
          />
        </div>
        <div v-for="file in sampleMenuFiles" :key="file.id" class="sample-menu-item d-flex justify-content-between align-items-center p-2 mb-2 border rounded">
          <div class="d-flex align-items-center text-truncate">
            <i :class="sampleMenuIcon(file)" class="mr-2"></i>
            <div class="text-truncate">
              <a :href="file.fileUrl" target="_blank" rel="noopener" class="font-weight-bold">{{ file.fileName }}</a>
              <div class="text-muted small">
                {{ formatFileSize(file.fileSize) }}<span v-if="file.createDatetime"> · {{ formatFileDate(file.createDatetime) }}</span>
              </div>
            </div>
          </div>
          <div class="flex-shrink-0 ml-2">
            <a :href="file.fileUrl" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary mr-1">
              <i class="fas fa-download"></i>
            </a>
            <button v-if="isDeletePermission" type="button" class="btn btn-sm btn-outline-danger" @click="removeSampleMenuFile(file)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
        <div v-if="!sampleMenuFiles.length" class="text-center text-muted py-3">
          Chưa có thực đơn mẫu.
        </div>
      </b-card>

    </b-overlay>
  </div>
</template>

<script>
import {
  getActionPlans,
  getBaseline,
  saveActionPlans,
  saveBaseline,
  saveGoalNote,
  deleteActionPlan,
  getKpiInputs,
  getSampleMenuFiles,
  uploadSampleMenuFiles,
  deleteSampleMenuFile,
} from '../services/services';
import { latestHba1c } from '../services/his-data';

const SAMPLE_MENU_MAX_SIZE = 100 * 1024 * 1024;
export default {
  name: 'Tab3BaselineGoal',
  props: {
    userPackageId: { type: String, default: null },
    hisBaselineData: { type: Object, default: null },
    packageStartDate: { type: Number, default: null },
    isViewPermission: { type: Boolean, default: false },
    isInsertPermission: { type: Boolean, default: false },
    isUpdatePermission: { type: Boolean, default: false },
    isDeletePermission: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      baselineForm: {
        height: null,
        weight: null,
        bmi: null,
        waist: null,
        hip: null,
        muscleMass: null,
        fatMass: null,
        bodyFatPercentage: null,
        visceralFatLevel: null,
        waterMass: null,
        hba1c: null,
        fastingBloodSugar: null,
        postprandialBloodSugar: null,
        systolicBloodPressure: null,
        diastolicBloodPressure: null,
        heartRate: null,
        bloodPressure: '',
        additionalNotes: '',
      },
      goalForm: {
        weightGoal: null,
        bmiGoal: null,
        waistGoal: null,
        hba1cGoal: null,
        fastingBloodSugarGoal: null,
        bloodPressureGoal: '',
        goalNote: '',
      },
      actionPlans: [],
      uidCounter: 0,
      sampleMenuFiles: [],
      sampleMenuUploading: false,
      sampleMenuAccept: '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx',
      // Tải xong baseline đã lưu → mới điền sẵn các ô còn trống từ HIS/App
      baselineLoaded: false,
      // Ngày bắt đầu gói đã dùng để lấy KPI App
      kpiLoadedFor: null,
      // field → giá trị tự điền từ App (HIS về sau được phép ghi đè)
      appFilled: {},
    };
  },
  mounted() {
    this.loadData();
    this.loadSampleMenuFiles();
  },
  watch: {
    hisBaselineData() {
      this.applySourceVitals();
    },
    // Ngày bắt đầu gói đến sau (weekList) → mới lấy chỉ số tại thời điểm vào gói
    packageStartDate() {
      this.applySourceVitals();
    },
  },
  methods: {
    isEmptyValue(value) {
      return value === null || value === undefined || value === '';
    },
    async applySourceVitals() {
      // Chưa có ngày bắt đầu gói → kpi-inputs?date=0 trả về chỉ số hiện tại, không phải ban đầu
      if (!this.baselineLoaded || !this.packageStartDate) return;
      this.applyHisVitals();
      if (this.kpiLoadedFor !== this.packageStartDate) {
        this.kpiLoadedFor = this.packageStartDate;
        await this.applyKpiVitals();
      }
      if (this.isEmptyValue(this.baselineForm.bmi)) this.calcBaselineBmi();
    },
    async applyKpiVitals() {
      if (!this.userPackageId) return;
      let kpi = null;
      try {
        const res = await getKpiInputs(this.userPackageId, this.packageStartDate);
        kpi = res?.data ?? res;
      } catch (_) {
        return;
      }
      if (!kpi) return;
      const f = this.baselineForm;
      // App chỉ điền field còn trống (HIS đã điền trước thì giữ HIS)
      const fill = (field, value) => {
        if (!this.isEmptyValue(f[field]) || this.isEmptyValue(value) || value === 0) return;
        f[field] = value;
        this.appFilled[field] = value;
      };
      const w = kpi.weight, bp = kpi.bloodPressure, bs = kpi.bloodSugar, h = kpi.hbA1c, ex = kpi.exercise;
      fill('height', w?.height);
      fill('weight', w?.weight);
      fill('bmi', w?.bmi);
      fill('waist', w?.waist);
      fill('systolicBloodPressure', bp?.systolic);
      fill('diastolicBloodPressure', bp?.diastolic);
      fill('heartRate', bp?.pulseRate);
      fill('fastingBloodSugar', bs?.fastingGlucose);
      fill('postprandialBloodSugar', bs?.postprandialGlucose);
      fill('hba1c', h?.hbA1c);
      if (ex && (ex.duration || ex.burnedCalorie)) {
        fill('additionalNotes', `Vận động: ${ex.duration || 0} phút, ${ex.burnedCalorie || 0} kcal`);
      }
    },
    applyHisVitals() {
      const his = this.hisBaselineData?.data;
      if (!his) return;
      const f = this.baselineForm;
      // Ưu tiên HIS: điền field trống, hoặc thay giá trị vừa tự điền từ App (coach chưa sửa)
      const set = (field, value) => {
        if (this.isEmptyValue(value) || value === 0) return;
        const fromApp = field in this.appFilled && String(f[field]) === String(this.appFilled[field]);
        if (!this.isEmptyValue(f[field]) && !fromApp) return;
        f[field] = value;
        delete this.appFilled[field];
      };
      const vital = his.vitalSigns?.[0];
      if (vital) {
        set('weight', vital.weight);
        set('height', vital.height);
        set('bmi', vital.bmi);
        set('waist', vital.waistCircumference);
        set('systolicBloodPressure', vital.bloodPressureSystolic);
        set('diastolicBloodPressure', vital.bloodPressureDiastolic);
        set('heartRate', vital.heartRate);
        const extras = [];
        if (vital.temperature) extras.push(`Nhiệt độ: ${vital.temperature} (°C)`);
        if (vital.respiratoryRate) extras.push(`Nhịp thở: ${vital.respiratoryRate} (lần/phút)`);
        if (vital.oxygenSaturation) extras.push(`SpO2: ${vital.oxygenSaturation} (%)`);
        if (extras.length) set('additionalNotes', extras.join('\n'));
      }
      // HbA1c từ kết quả xét nghiệm HIS
      const hba1c = latestHba1c(his);
      if (hba1c) set('hba1c', hba1c.value);
    },
    parseBloodPressure(value) {
      const match = String(value || '').match(/(\d+)\s*\/\s*(\d+)/);
      return {
        systolic: match ? Number(match[1]) : null,
        diastolic: match ? Number(match[2]) : null,
      };
    },
    formatBloodPressure(systolic, diastolic) {
      const hasSystolic = systolic !== null && systolic !== undefined && systolic !== '';
      const hasDiastolic = diastolic !== null && diastolic !== undefined && diastolic !== '';
      return hasSystolic && hasDiastolic ? `${systolic}/${diastolic}` : '';
    },
    async loadData() {
      this.loading = true;
      try {
        const [baselineRes, actionRes] = await Promise.all([
          getBaseline(this.userPackageId),
          getActionPlans(this.userPackageId),
        ]);
        const bd = baselineRes?.data ?? baselineRes;
        if (bd?.baseline) {
          const b = bd.baseline;
          const parsedBloodPressure = this.parseBloodPressure(b.bloodPressure);
          this.baselineForm = {
            height: b.height ? Math.round(b.height * 100) : null,
            weight: b.weight ?? null,
            bmi: b.bmi ?? null,
            waist: b.waist ?? null,
            hip: b.hip ?? null,
            muscleMass: b.muscleMass ?? null,
            fatMass: b.fatMass ?? null,
            bodyFatPercentage: b.bodyFatPercentage ?? null,
            visceralFatLevel: b.visceralFatLevel ?? null,
            waterMass: b.waterMass ?? null,
            hba1c: b.hba1c ?? null,
            fastingBloodSugar: b.fastingBloodSugar ?? null,
            postprandialBloodSugar: b.postprandialBloodSugar ?? null,
            systolicBloodPressure: b.systolicBloodPressure ?? parsedBloodPressure.systolic,
            diastolicBloodPressure: b.diastolicBloodPressure ?? parsedBloodPressure.diastolic,
            heartRate: b.heartRate ?? null,
            bloodPressure: b.bloodPressure || '',
            additionalNotes: b.additionalNotes || '',
          };
        }
        if (bd?.goalNote) {
          const g = bd.goalNote;
          this.goalForm = {
            weightGoal: g.weightGoal ?? null,
            bmiGoal: g.bmiGoal ?? null,
            waistGoal: g.waistGoal ?? null,
            hba1cGoal: g.hba1cGoal ?? null,
            fastingBloodSugarGoal: g.fastingBloodSugarGoal ?? null,
            bloodPressureGoal: g.bloodPressureGoal || '',
            goalNote: g.goalNote || '',
          };
        }
        this.baselineLoaded = true;
        // Điền field trống: HIS (trước ngày vào gói) ưu tiên, KPI App dự phòng
        await this.applySourceVitals();
        this.calcGoalBmi();
        const plans = actionRes?.data ?? actionRes ?? [];
        this.actionPlans = plans.map(p => ({
          id: p.id || null,
          action: p.action || '',
          frequency: p.frequency ?? null,
          unit: p.unit || '',
          note: p.note || '',
          dirty: false,
          _uid: this.nextUid(),
        }));
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải dữ liệu.' });
      } finally {
        this.loading = false;
      }
    },
    calcBaselineBmi() {
      const h = parseFloat(this.baselineForm.height);
      const w = parseFloat(this.baselineForm.weight);
      if (h > 0 && w > 0) {
        const hm = h / 100;
        this.baselineForm.bmi = Math.round((w / (hm * hm)) * 10) / 10;
      }
      this.calcGoalBmi();
    },
    calcGoalBmi() {
      const h = parseFloat(this.baselineForm.height);
      const w = parseFloat(this.goalForm.weightGoal);
      if (h > 0 && w > 0) {
        const hm = h / 100;
        this.goalForm.bmiGoal = Math.round((w / (hm * hm)) * 10) / 10;
      }
    },
    async saveBaseline() {
      await saveBaseline({
        userPackageId: this.userPackageId,
        ...this.baselineForm,
        height: this.baselineForm.height ? this.baselineForm.height / 100 : null,
        bloodPressure: this.formatBloodPressure(
          this.baselineForm.systolicBloodPressure,
          this.baselineForm.diastolicBloodPressure,
        ),
      });
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu tổng trạng ban đầu.' });
    },
    async saveGoalNote() {
      await saveGoalNote({ userPackageId: this.userPackageId, ...this.goalForm });
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu ghi chú mục tiêu.' });
    },
    async saveOnePlan(idx) {
      const plan = this.actionPlans[idx];
      const confirmed = await this.$swal({
        title: 'Xác nhận lưu',
        text: `Bạn có chắc muốn lưu kế hoạch "${plan.action || 'này'}"?`,
        icon: 'warning',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, closeModal: true },
          confirm: { text: 'Lưu', value: true, visible: true, closeModal: true },
        },
      });
      if (!confirmed) return;
      const res = await saveActionPlans({
        userPackageId: this.userPackageId,
        actionPlans: [{ id: plan.id, action: plan.action, frequency: plan.frequency, unit: plan.unit, note: plan.note }],
      });
      const saved = (res?.data ?? res ?? [])[0];
      this.$set(this.actionPlans, idx, {
        ...this.actionPlans[idx],
        id: saved?.id ?? this.actionPlans[idx].id,
        dirty: false,
      });
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu kế hoạch.' });
    },
    async loadSampleMenuFiles() {
      if (!this.userPackageId) return;
      try {
        const res = await getSampleMenuFiles(this.userPackageId);
        this.sampleMenuFiles = res?.data ?? res ?? [];
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải thực đơn mẫu.' });
      }
    },
    async onSampleMenuSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (!files.length) return;
      const tooLarge = files.find(f => f.size > SAMPLE_MENU_MAX_SIZE);
      if (tooLarge) {
        this.$toastr.e({ title: 'Lỗi', msg: `File "${tooLarge.name}" vượt quá 100MB.` });
        return;
      }
      const formData = new FormData();
      formData.append('UserPackageId', this.userPackageId);
      files.forEach(f => formData.append('Files', f));
      this.sampleMenuUploading = true;
      try {
        const res = await uploadSampleMenuFiles(formData);
        this.sampleMenuFiles = res?.data ?? res ?? [];
        this.$toastr.s({ title: 'Thành công', msg: 'Đã đính kèm thực đơn mẫu.' });
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải file lên.' });
      } finally {
        this.sampleMenuUploading = false;
      }
    },
    async removeSampleMenuFile(file) {
      const confirmed = await this.$swal({
        title: 'Xác nhận xóa',
        text: `Bạn có chắc muốn xóa file "${file.fileName}"?`,
        icon: 'warning',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, closeModal: true },
          confirm: { text: 'Xóa', value: true, visible: true, closeModal: true },
        },
      });
      if (!confirmed) return;
      await deleteSampleMenuFile(file.id);
      this.sampleMenuFiles = this.sampleMenuFiles.filter(f => f.id !== file.id);
      this.$toastr.s({ title: 'Thành công', msg: 'Đã xóa file.' });
    },
    sampleMenuIcon(file) {
      const mime = file.mimeType || '';
      if (mime.includes('pdf')) return 'fas fa-file-pdf text-danger';
      if (mime.startsWith('image/')) return 'fas fa-file-image text-info';
      if (mime.includes('sheet') || mime.includes('excel')) return 'fas fa-file-excel text-success';
      if (mime.includes('word')) return 'fas fa-file-word text-primary';
      return 'fas fa-file text-muted';
    },
    formatFileSize(size) {
      if (!size) return '';
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatFileDate(value) {
      if (!value) return '';
      const raw = String(value);
      let date;
      if (/^\d+$/.test(raw)) date = new Date(Number(raw) * 1000);
      else date = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`);
      return isNaN(date.getTime()) ? '' : date.toLocaleString('vi-VN');
    },
    nextUid() {
      return ++this.uidCounter;
    },
    markDirty(idx) {
      this.actionPlans[idx].dirty = true;
    },
    addActionPlan() {
      this.actionPlans.push({
        id: null,
        action: '',
        frequency: null,
        unit: '',
        note: '',
        dirty: true,
        _uid: this.nextUid(),
      });
    },
    async removeActionPlan(idx) {
      const plan = this.actionPlans[idx];
      const confirmed = await this.$swal({
        title: 'Xác nhận xóa',
        text: `Bạn có chắc muốn xóa kế hoạch "${plan.action || 'này'}"?`,
        icon: 'warning',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, closeModal: true },
          confirm: { text: 'Xóa', value: true, visible: true, closeModal: true },
        },
      });
      if (!confirmed) return;
      if (plan.id) {
        await deleteActionPlan(plan.id);
      }
      this.actionPlans.splice(idx, 1);
    },
  },
};
</script>

<style scoped>
.metrics-card {
  border: 1px solid #EBEDF3;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 30px 0 rgba(82, 63, 105, 0.05);
}

.metrics-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #181C32;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.metrics-title i {
  color: #3699FF;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 16px;
}

.sample-menu-item {
  background: #F3F6F9;
  border-color: #EBEDF3 !important;
}

.action-plan-card {
  background: #F3F6F9;
  border-color: #EBEDF3 !important;
}

.plan-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

@media (max-width: 767.98px) {
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 575.98px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
